import React from 'react';

import { FEATURE_TONE_COLORS, type FeatureTone } from '../../theme/tokens';
import { VsMarketBar } from '../VsMarketBar/VsMarketBar';
import type { ButtonVariant } from '../Button/Button';
import {
  Root,
  Card,
  TopRow,
  Identity,
  ArtWrap,
  Art,
  AlertDot,
  Name,
  Status,
  BarSlot,
  Benefits,
  BenefitsInner,
  UnlockHeader,
  UnlockLine,
  UnlockLabel,
  BenefitRow,
  Benefit,
  BenefitDelta,
  BenefitValue,
  BenefitLabel,
  Cta,
} from './FeatureCard.styles';

export type FeatureBenefit = {
  id: string;
  /** Delta figure, sign included ("+7"). */
  value: string;
  label: string;
};

export type FeatureCardProps = {
  /** Feature artwork URL. */
  image: string;
  /** Feature name (API value, rendered through <Text>). */
  name: string;
  /** Status line under the name ("Inactive", "Level {{level}}/{{max}}"). */
  statusText: string;
  statusPlaceholders?: Record<string, string | number>;
  /** Colours the status line. The comparison bar is deliberately NOT toned — it follows the
   *  find-my-pack language, where one fill colour serves every feature. */
  tone: FeatureTone;
  /** You-vs-Market comparison: fill %, market marker %. */
  bar: { value: number; market: number };
  /** Tier name for the pill riding the bar's fill tip: the active tier of an enabled feature, or
   *  the tier an inactive one would activate. Omit for no pill. */
  tierLabel?: string;
  /** Benefits overline ("Activate {{name}} to unlock"). */
  unlockText: string;
  unlockPlaceholders?: Record<string, string | number>;
  /** Benefit deltas under the unlock divider. Pass an empty array to show just the
   *  divider with no figures (e.g. a fully-enabled feature with only +0 deltas). */
  benefits: FeatureBenefit[];
  /** Leave out the unlock divider and its deltas altogether, so the identity + bar region runs up to the CTA
   *  (e.g. the Newton showcase, which shows no scores). */
  hideBenefits?: boolean;
  ctaLabel: string;
  /** CTA pill style. Defaults to `outline` (the standard card action); pass `primary` for a
   *  filled, higher-emphasis action such as finishing a pending payment. */
  ctaVariant?: ButtonVariant;
  /** Show an attention dot on the artwork (e.g. a feature with a payment to finish) — mirrors
   *  the Product sidebar dot. */
  alert?: boolean;
  /** Opens the feature. Wired to BOTH the pill CTA and (as a whole-card affordance)
   *  the card surface, so a click anywhere on the card opens the same detail page. */
  onCta?: (event: React.MouseEvent<HTMLElement>) => void;
  /** When false the comparison bar stays empty (pre-mount state). */
  animate: boolean;
  /** Stagger offset in ms so a list of cards fills in sequence. */
  delay?: number;
  className?: string;
};

/**
 * Missing/enabled feature row: artwork + name + toned status on the left, a
 * You-vs-Market comparison bar, the unlock-benefit deltas, and a pill CTA.
 */
export const FeatureCard = ({
  image,
  name,
  statusText,
  statusPlaceholders,
  tone,
  bar,
  tierLabel,
  unlockText,
  unlockPlaceholders,
  benefits,
  hideBenefits = false,
  ctaLabel,
  ctaVariant = 'outline',
  alert = false,
  onCta,
  animate,
  delay = 0,
  className,
}: FeatureCardProps): React.ReactElement => {
  const clickable = Boolean(onCta);
  // The whole card and the pill CTA open the same page. Stop the pill's click from
  // bubbling to the card so the action fires exactly once (and let the CTA stay the
  // keyboard/AT-focusable control, so the card surface is a pointer affordance only,
  // never a `role="button"` nested around the real <button>).
  const onCtaClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    onCta?.(event);
  };
  return (
    // data-lumio-group: marks the card as one unit for the Lumio page-context
    // serializer, so each feature's numbers stay attributed to its name
    // (see src/nova/lib/lumioPageContext.ts).
    <Root className={className} data-lumio-group="feature-card">
      <Card $clickable={clickable} onClick={onCta}>
        <TopRow>
          <Identity>
            <ArtWrap>
              <Art src={image} alt="" />
              {alert ? <AlertDot aria-hidden="true" /> : null}
            </ArtWrap>
            <Name text={name} tag="span" title={name} />
            <Status text={statusText} placeholders={statusPlaceholders} tag="span" $color={FEATURE_TONE_COLORS[tone]} />
          </Identity>
          <BarSlot>
            <VsMarketBar value={bar.value} market={bar.market} tierLabel={tierLabel} animate={animate} delay={delay} />
          </BarSlot>
        </TopRow>
        {hideBenefits ? null : (
          <Benefits>
            <BenefitsInner>
              <UnlockHeader>
                <UnlockLine />
                <UnlockLabel text={unlockText} placeholders={unlockPlaceholders} tag="span" />
                <UnlockLine />
              </UnlockHeader>
              {benefits.length > 0 ? (
                <BenefitRow>
                  {benefits.map((benefit) => (
                    <Benefit key={benefit.id}>
                      <BenefitDelta>
                        <BenefitValue text={benefit.value} tag="span" />
                      </BenefitDelta>
                      <BenefitLabel text={benefit.label} tag="span" />
                    </Benefit>
                  ))}
                </BenefitRow>
              ) : null}
            </BenefitsInner>
          </Benefits>
        )}
        <Cta label={ctaLabel} variant={ctaVariant} onClick={onCtaClick} />
      </Card>
    </Root>
  );
};

export default FeatureCard;
